import React, { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { TRIP_STATES } from 'src/inbox/constants';
import dateTimeDurationSelector from 'src/inbox/selectors/dateTimeDuration/dateTimeDuration';
import { TRIP_ACTIONS } from 'src/inbox/type/TripAction';
import { CalendarAction } from '../../type/CalendarAction';
import { ImmutableTrip } from '../../../types/trips/Trips';
import NotificationMessage from '../../../common/components/NotificationMessage';
import { getActions } from '../TripMenu/actions';
import s from './DateUnavailable.module.scss';

type Props = {
  trip: ImmutableTrip;
  classNameModifier?: string;
};

const DateUnavailable: FC<Props> = ({ trip, classNameModifier = '' }) => {
  const dispatch = useDispatch();

  const tripState = trip.getIn(['state', 'state']);
  const isOfferCancelled = (
    tripState === TRIP_STATES.OWNER_OFFER_CANCELLED
    || tripState === TRIP_STATES.OWNER_OFFER_AUTO_CANCELLED
  );
  const isOfferExpired = (
    tripState === TRIP_STATES.OWNER_OFFER_EXPIRED
    || tripState === TRIP_STATES.OWNER_CUSTOM_OFFER_EXPIRED
  );
  const hasCalendarConflicts = trip.get('has_calendar_conflicts');
  const { tripOnly: { sameDates } } = useSelector(dateTimeDurationSelector(trip.get('pk')));
  const calendarAction = getActions(tripState).find(
    (action) => action.name === TRIP_ACTIONS.OPEN_IN_CALENDAR,
  ) as CalendarAction;

  const modifiers = [
    classNameModifier,
    'alignTop',
    'largerFontSize',
    'largerBottomMargin',
    'indent',
    'inbox',
  ].join(' ');

  const getWarningText = () => {
    if (!sameDates) {
      return 'Some periods are marked as unavailable by other events in your calendar.';
    }
    return 'This period is marked as unavailable by another event in your calendar.';
  };

  if (hasCalendarConflicts) {
    return (
      <NotificationMessage
        outdent={false}
        classNameModifier={modifiers}
        variant={isOfferCancelled || isOfferExpired ? 'info' : 'warning'}
      >
        {getWarningText()}{' '}
        <Link
          to={calendarAction.url}
          onClick={() => calendarAction.onClick(dispatch, trip)}
          className={s.inlineLink}
        >
          {calendarAction.title}
        </Link>
      </NotificationMessage>
    );
  }

  return null;
};

export default DateUnavailable;
